import { useMutation, useQueryClient } from 'react-query'
import { BASE_API_URL } from 'constants/env'
import { API_METHODS } from 'constants/api-methods'
import { apiHeaders } from 'utils/api-headers'
import { store } from 'index'
import { handleErrors } from 'utils/handle-errors'
import { PnsMacMiniUserStatus, WispStatus } from 'interfaces/pns-mac-mini-user'
import toast from 'react-hot-toast'

interface CreateMacMiniUserParams {
  macMiniId: string
  username: string
  status: PnsMacMiniUserStatus
  wispPort: number
}

interface CreateMacMiniUsersParams {
  macMiniId: string
  users: Array<{
    username: string
    status: PnsMacMiniUserStatus
    wispPort: number
  }>
}

const createSingleMacMiniUser = async (params: CreateMacMiniUserParams) => {
  const { macMiniId, username, status, wispPort } = params

  const requestBody = {
    macMiniId,
    username,
    passwordHash: 'default_hashed_password', // This would be generated securely in real implementation
    wispPort,
    status,
    wispStatus: WispStatus.STOPPED, // Default WISP status
  }

  let response = await fetch(
    `${BASE_API_URL}/api/v4/phone_number_service/mac_mini_users`,
    {
      method: API_METHODS.POST,
      headers: apiHeaders(store.getState()),
      body: JSON.stringify(requestBody),
    }
  )

  response = handleErrors(response)

  return response.json()
}

const createMacMiniUsers = async (params: CreateMacMiniUsersParams) => {
  const { macMiniId, users } = params

  // Create users sequentially with delay to avoid port conflicts and server overload
  // Using reduce instead of for loop to satisfy ESLint no-await-in-loop rule
  const createdUsers = await users.reduce(
    async (previousPromise, user, index) => {
      const createdUsersSoFar = await previousPromise

      try {
        const createdUser = await createSingleMacMiniUser({
          macMiniId,
          username: user.username,
          status: user.status,
          wispPort: user.wispPort,
        })
        createdUsersSoFar.push(createdUser)

        // Small delay between requests to avoid overwhelming the server
        if (index < users.length - 1) {
          await new Promise<void>((resolve) => {
            setTimeout(resolve, 200)
          })
        }

        return createdUsersSoFar
      } catch (error) {
        console.error(`Failed to create user ${user.username}:`, error)
        throw new Error(
          `Failed to create user ${user.username}: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`
        )
      }
    },
    Promise.resolve([] as any[])
  )

  return {
    users: createdUsers,
    totalCreated: createdUsers.length,
    macMiniId,
  }
}

const useCreateMacMiniUsers = () => {
  const queryClient = useQueryClient()

  return useMutation(createMacMiniUsers, {
    onError: (error: Error) => {
      console.error('Failed to create Mac Mini users:', error)
      toast.error(`Failed to create Mac Mini users: ${error.message}`)
    },
    onSuccess: (data) => {
      const { totalCreated, macMiniId } = data
      toast.success(
        `Successfully created ${totalCreated} Mac Mini user${
          totalCreated === 1 ? '' : 's'
        }`
      )

      // Invalidate related queries
      queryClient.invalidateQueries(['mac-mini-detail', macMiniId])
      queryClient.invalidateQueries(['mac-mini-users', macMiniId])
      queryClient.invalidateQueries(['mac-minis-by-ansible'])
      queryClient.invalidateQueries(['ansible-detail'])
    },
  })
}

export default useCreateMacMiniUsers
